'use client'
import { useState, useRef, useEffect, type KeyboardEvent } from 'react'
import { Search, X } from 'lucide-react'
import {
  findCategories,
  getAllManufacturers,
  getAllSections,
  getAllTags,
} from '@/lib/actions'
import './styles.scss'

interface Option {
  slug: string
  title: string
}

type FilterField = 'tag' | 'section' | 'category' | 'manufacturer'

interface ProductsFilterProps {
  initialSections: Option[]
  initialTags: Option[]
  initialCategories: Option[]
  initialManufacturers: Option[]
  initialQuery: string
  initialSection: string
  initialCategory: string
  initialTag: string
  initialManufacturer: string
  onNavigateTo: (url: string) => void
  isNavigating?: boolean
  basePath?: string
  hideTags?: boolean
}

export function ProductsFilter({
  initialSections,
  initialTags,
  initialCategories,
  initialManufacturers,
  initialQuery,
  initialSection,
  initialCategory,
  initialTag,
  initialManufacturer,
  onNavigateTo,
  isNavigating = false,
  basePath = '/products',
  hideTags = false,
}: ProductsFilterProps) {
  const isPending = isNavigating

  const [query, setQuery] = useState(initialQuery)

  // Mutable option lists (updated on cross-filter)
  const [sections, setSections] = useState<Option[]>(initialSections)
  const [tags, setTags] = useState<Option[]>(initialTags)
  const [categories, setCategories] = useState<Option[]>(initialCategories)
  const [manufacturers, setManufacturers] =
    useState<Option[]>(initialManufacturers)

  // Text inputs (what user sees/types)
  const [sectionInput, setSectionInput] = useState(
    initialSections.find((s) => s.slug === initialSection)?.title ?? '',
  )
  const [tagInput, setTagInput] = useState(
    initialTags.find((t) => t.slug === initialTag)?.title ?? '',
  )
  const [categoryInput, setCategoryInput] = useState(
    initialCategories.find((c) => c.slug === initialCategory)?.title ?? '',
  )
  const [manufacturerInput, setManufacturerInput] = useState(
    initialManufacturers.find((m) => m.slug === initialManufacturer)?.title ??
      '',
  )

  // Selected slugs (used in search params)
  const [selectedSection, setSelectedSection] = useState(initialSection)
  const [selectedCategory, setSelectedCategory] = useState(initialCategory)
  const [selectedTag, setSelectedTag] = useState(initialTag)
  const [selectedManufacturer, setSelectedManufacturer] =
    useState(initialManufacturer)

  // Loading states
  const [loadingCategories, setLoadingCategories] = useState(false)
  const [loadingSections, setLoadingSections] = useState(false)
  const [loadingTags, setLoadingTags] = useState(false)
  const [loadingManufacturers, setLoadingManufacturers] = useState(false)

  // Custom dropdown state
  const [openField, setOpenField] = useState<FilterField | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const filterRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setOpenField(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Reset only when the typed text changes — NOT on openField changes,
  // because ArrowDown batches setOpenField + setActiveIndex in the same render
  // and a useEffect on openField would race and override the new index.
  useEffect(() => {
    setActiveIndex(-1)
  }, [tagInput, sectionInput, categoryInput, manufacturerInput])

  // ─── Tag change ──────────────────────────────────────────────────────────

  const handleTagChange = async (value: string) => {
    setTagInput(value)
    const match = tags.find(
      (t) => t.title.toLowerCase() === value.toLowerCase(),
    )
    const tagSlug = match?.slug ?? ''
    setSelectedTag(tagSlug)

    if (tagSlug) {
      // sections: section → categories → products → tags
      setLoadingSections(true)
      const sectionProdFilter: Record<string, unknown> = {
        tags: { slug: tagSlug },
        ...(selectedManufacturer
          ? { manufacturer: { slug: selectedManufacturer } }
          : {}),
      }
      const sectionsResult = await getAllSections({
        categories: { products: sectionProdFilter },
      })
      if (!('error' in sectionsResult) && sectionsResult.data.length > 0) {
        const newSections = sectionsResult.data.map((s) => ({
          slug: s.attributes.slug,
          title: s.attributes.title,
        }))
        setSections(newSections)
        if (
          selectedSection &&
          !newSections.find((s) => s.slug === selectedSection)
        ) {
          setSelectedSection('')
          setSectionInput('')
          setSelectedCategory('')
          setCategoryInput('')
          setCategories([])
        } else if (selectedSection) {
          setLoadingCategories(true)
          const catResult = await findCategories(
            {
              section: { slug: selectedSection },
              products: {
                tags: { slug: tagSlug },
                ...(selectedManufacturer
                  ? { manufacturer: { slug: selectedManufacturer } }
                  : {}),
              },
            },
            { page: 1, pageSize: 100 },
          )
          if (!('error' in catResult)) {
            setCategories(
              catResult.data.map((c) => ({
                slug: c.attributes.slug,
                title: c.attributes.title,
              })),
            )
          }
          setLoadingCategories(false)
        }
      } else if (selectedSection) {
        // filter returned nothing — re-filter categories safely without clearing section
        setLoadingCategories(true)
        const catResult = await findCategories(
          {
            section: { slug: selectedSection },
            products: { tags: { slug: tagSlug } },
          },
          { page: 1, pageSize: 100 },
        )
        if (!('error' in catResult)) {
          setCategories(
            catResult.data.map((c) => ({
              slug: c.attributes.slug,
              title: c.attributes.title,
            })),
          )
        }
        setLoadingCategories(false)
      }
      setLoadingSections(false)

      // Cross-filter manufacturers: products with this tag (+ section if active)
      setLoadingManufacturers(true)
      const manuFilters: Record<string, unknown> = {
        products: {
          tags: { slug: tagSlug },
          ...(selectedSection
            ? { categories: { section: { slug: selectedSection } } }
            : {}),
        },
      }
      const manuResult = await getAllManufacturers(manuFilters)
      if (!('error' in manuResult) && manuResult.data.length > 0) {
        const newManus = manuResult.data.map((m) => ({
          slug: m.attributes.slug,
          title: m.attributes.title,
        }))
        setManufacturers(newManus)
        if (
          selectedManufacturer &&
          !newManus.find((m) => m.slug === selectedManufacturer)
        ) {
          setSelectedManufacturer('')
          setManufacturerInput('')
        }
      }
      setLoadingManufacturers(false)
    } else {
      // Tag cleared — restore based on remaining constraints
      const sectionFilters: Record<string, unknown> = selectedManufacturer
        ? {
            categories: {
              products: { manufacturer: { slug: selectedManufacturer } },
            },
          }
        : {}
      const sectionsResult = await getAllSections(sectionFilters)
      if (!('error' in sectionsResult) && sectionsResult.data.length > 0) {
        setSections(
          sectionsResult.data.map((s) => ({
            slug: s.attributes.slug,
            title: s.attributes.title,
          })),
        )
      } else {
        setSections(initialSections)
      }

      if (selectedSection) {
        setLoadingCategories(true)
        const catFilters: Record<string, unknown> = {
          section: { slug: selectedSection },
          ...(selectedManufacturer
            ? { products: { manufacturer: { slug: selectedManufacturer } } }
            : {}),
        }
        const catResult = await findCategories(catFilters, {
          page: 1,
          pageSize: 100,
        })
        if (!('error' in catResult)) {
          setCategories(
            catResult.data.map((c) => ({
              slug: c.attributes.slug,
              title: c.attributes.title,
            })),
          )
        }
        setLoadingCategories(false)
      } else {
        setCategories(initialCategories)
      }

      // Restore manufacturers based on section only
      const manuFilters: Record<string, unknown> = selectedSection
        ? { products: { categories: { section: { slug: selectedSection } } } }
        : {}
      const manuResult = await getAllManufacturers(manuFilters)
      if (!('error' in manuResult) && manuResult.data.length > 0) {
        setManufacturers(
          manuResult.data.map((m) => ({
            slug: m.attributes.slug,
            title: m.attributes.title,
          })),
        )
      } else {
        setManufacturers(initialManufacturers)
      }
    }
  }

  // ─── Section change ──────────────────────────────────────────────────────

  const handleSectionChange = async (value: string) => {
    setSectionInput(value)
    const match = sections.find(
      (s) => s.title.toLowerCase() === value.toLowerCase(),
    )

    if (match) {
      if (match.slug === selectedSection) return
      setSelectedSection(match.slug)
      setSelectedCategory('')
      setCategoryInput('')

      // Cross-filter categories: section (+ tag + manufacturer)
      setLoadingCategories(true)
      const catFilters: Record<string, unknown> = {
        section: { slug: match.slug },
      }
      const prodSub: Record<string, unknown> = {}
      if (selectedTag) prodSub.tags = { slug: selectedTag }
      if (selectedManufacturer)
        prodSub.manufacturer = { slug: selectedManufacturer }
      if (Object.keys(prodSub).length) catFilters.products = prodSub
      const catResult = await findCategories(catFilters, {
        page: 1,
        pageSize: 100,
      })
      if (!('error' in catResult)) {
        setCategories(
          catResult.data.map((c) => ({
            slug: c.attributes.slug,
            title: c.attributes.title,
          })),
        )
      }
      setLoadingCategories(false)

      // Cross-filter tags: products in this section (+ manufacturer)
      if (!hideTags) {
        setLoadingTags(true)
        const tagFilters: Record<string, unknown> = {
          products: {
            categories: { section: { slug: match.slug } },
            ...(selectedManufacturer
              ? { manufacturer: { slug: selectedManufacturer } }
              : {}),
          },
        }
        const tagsResult = await getAllTags(tagFilters)
        if (!('error' in tagsResult)) {
          const newTags = tagsResult.data.map((t) => ({
            slug: t.attributes.slug,
            title: t.attributes.title,
          }))
          setTags(newTags)
          if (selectedTag && !newTags.find((t) => t.slug === selectedTag)) {
            setSelectedTag('')
            setTagInput('')
          }
        }
        setLoadingTags(false)
      }

      // Cross-filter manufacturers: products in this section (+ tag)
      setLoadingManufacturers(true)
      const manuFilters: Record<string, unknown> = {
        products: {
          categories: { section: { slug: match.slug } },
          ...(selectedTag ? { tags: { slug: selectedTag } } : {}),
        },
      }
      const manuResult = await getAllManufacturers(manuFilters)
      if (!('error' in manuResult)) {
        const newManus = manuResult.data.map((m) => ({
          slug: m.attributes.slug,
          title: m.attributes.title,
        }))
        setManufacturers(newManus)
        if (
          selectedManufacturer &&
          !newManus.find((m) => m.slug === selectedManufacturer)
        ) {
          setSelectedManufacturer('')
          setManufacturerInput('')
        }
      }
      setLoadingManufacturers(false)
    } else {
      // Section cleared
      setSelectedSection('')
      setSelectedCategory('')
      setCategoryInput('')
      setCategories([])

      // Restore tags based on manufacturer only
      if (!hideTags) {
        const tagFilters: Record<string, unknown> = selectedManufacturer
          ? { products: { manufacturer: { slug: selectedManufacturer } } }
          : {}
        const tagsResult = await getAllTags(tagFilters)
        if (!('error' in tagsResult)) {
          setTags(
            tagsResult.data.map((t) => ({
              slug: t.attributes.slug,
              title: t.attributes.title,
            })),
          )
        } else {
          setTags(initialTags)
        }
      }

      // Restore manufacturers based on tag only
      const manuFilters: Record<string, unknown> = selectedTag
        ? { products: { tags: { slug: selectedTag } } }
        : {}
      const manuResult = await getAllManufacturers(manuFilters)
      if (!('error' in manuResult)) {
        setManufacturers(
          manuResult.data.map((m) => ({
            slug: m.attributes.slug,
            title: m.attributes.title,
          })),
        )
      } else {
        setManufacturers(initialManufacturers)
      }
    }
  }

  // ─── Category change ─────────────────────────────────────────────────────

  const handleCategoryChange = (value: string) => {
    setCategoryInput(value)
    const match = categories.find(
      (c) => c.title.toLowerCase() === value.toLowerCase(),
    )
    setSelectedCategory(match?.slug ?? '')
  }

  // ─── Manufacturer change ─────────────────────────────────────────────────

  const handleManufacturerChange = async (value: string) => {
    setManufacturerInput(value)
    const match = manufacturers.find(
      (m) => m.title.toLowerCase() === value.toLowerCase(),
    )
    const manufacturerSlug = match?.slug ?? ''
    setSelectedManufacturer(manufacturerSlug)

    if (manufacturerSlug) {
      // sections: section → categories → products → manufacturer
      setLoadingSections(true)
      const sectionProdFilter: Record<string, unknown> = {
        manufacturer: { slug: manufacturerSlug },
        ...(selectedTag ? { tags: { slug: selectedTag } } : {}),
      }
      const sectionsResult = await getAllSections({
        categories: { products: sectionProdFilter },
      })
      if (!('error' in sectionsResult) && sectionsResult.data.length > 0) {
        const newSections = sectionsResult.data.map((s) => ({
          slug: s.attributes.slug,
          title: s.attributes.title,
        }))
        setSections(newSections)
        if (
          selectedSection &&
          !newSections.find((s) => s.slug === selectedSection)
        ) {
          setSelectedSection('')
          setSectionInput('')
          setSelectedCategory('')
          setCategoryInput('')
          setCategories([])
        } else if (selectedSection) {
          setLoadingCategories(true)
          const catResult = await findCategories(
            {
              section: { slug: selectedSection },
              products: {
                manufacturer: { slug: manufacturerSlug },
                ...(selectedTag ? { tags: { slug: selectedTag } } : {}),
              },
            },
            { page: 1, pageSize: 100 },
          )
          if (!('error' in catResult)) {
            setCategories(
              catResult.data.map((c) => ({
                slug: c.attributes.slug,
                title: c.attributes.title,
              })),
            )
          }
          setLoadingCategories(false)
        }
      } else if (selectedSection) {
        // filter path returned nothing — still re-filter categories safely
        setLoadingCategories(true)
        const catResult = await findCategories(
          {
            section: { slug: selectedSection },
            products: { manufacturer: { slug: manufacturerSlug } },
          },
          { page: 1, pageSize: 100 },
        )
        if (!('error' in catResult)) {
          setCategories(
            catResult.data.map((c) => ({
              slug: c.attributes.slug,
              title: c.attributes.title,
            })),
          )
        }
        setLoadingCategories(false)
      }
      setLoadingSections(false)

      // Cross-filter tags: products from this manufacturer (+ section if active)
      if (!hideTags) {
        setLoadingTags(true)
        const tagFilters: Record<string, unknown> = {
          products: {
            manufacturer: { slug: manufacturerSlug },
            ...(selectedSection
              ? { categories: { section: { slug: selectedSection } } }
              : {}),
          },
        }
        const tagsResult = await getAllTags(tagFilters)
        if (!('error' in tagsResult) && tagsResult.data.length > 0) {
          const newTags = tagsResult.data.map((t) => ({
            slug: t.attributes.slug,
            title: t.attributes.title,
          }))
          setTags(newTags)
          if (selectedTag && !newTags.find((t) => t.slug === selectedTag)) {
            setSelectedTag('')
            setTagInput('')
          }
        }
        setLoadingTags(false)
      }
    } else {
      // Manufacturer cleared — restore sections and tags based on remaining filters
      const sectionFilters: Record<string, unknown> = selectedTag
        ? { categories: { products: { tags: { slug: selectedTag } } } }
        : {}
      const sectionsResult = await getAllSections(sectionFilters)
      if (!('error' in sectionsResult) && sectionsResult.data.length > 0) {
        setSections(
          sectionsResult.data.map((s) => ({
            slug: s.attributes.slug,
            title: s.attributes.title,
          })),
        )
      } else {
        setSections(initialSections)
      }

      if (selectedSection) {
        setLoadingCategories(true)
        const catFilters: Record<string, unknown> = {
          section: { slug: selectedSection },
          ...(selectedTag ? { products: { tags: { slug: selectedTag } } } : {}),
        }
        const catResult = await findCategories(catFilters, {
          page: 1,
          pageSize: 100,
        })
        if (!('error' in catResult)) {
          setCategories(
            catResult.data.map((c) => ({
              slug: c.attributes.slug,
              title: c.attributes.title,
            })),
          )
        }
        setLoadingCategories(false)
      } else {
        setCategories(initialCategories)
      }

      // Restore tags based on section only
      if (!hideTags) {
        const tagFilters: Record<string, unknown> = selectedSection
          ? { products: { categories: { section: { slug: selectedSection } } } }
          : {}
        const tagsResult = await getAllTags(tagFilters)
        if (!('error' in tagsResult) && tagsResult.data.length > 0) {
          setTags(
            tagsResult.data.map((t) => ({
              slug: t.attributes.slug,
              title: t.attributes.title,
            })),
          )
        } else {
          setTags(initialTags)
        }
      }
    }
  }

  // ─── Search / Clear ──────────────────────────────────────────────────────

  const handleSearch = () => {
    const params = new URLSearchParams()
    if (query.trim()) params.set('q', query.trim())
    if (selectedSection) params.set('section', selectedSection)
    if (selectedCategory) params.set('category', selectedCategory)
    if (!hideTags && selectedTag) params.set('tag', selectedTag)
    if (selectedManufacturer) params.set('manufacturer', selectedManufacturer)
    const qs = params.toString()
    onNavigateTo(`${basePath}/1${qs ? `?${qs}` : ''}`)
  }

  const handleClear = () => {
    setQuery('')
    setSectionInput('')
    setTagInput('')
    setCategoryInput('')
    setManufacturerInput('')
    setSelectedSection('')
    setSelectedCategory('')
    setSelectedManufacturer('')
    if (!hideTags) setSelectedTag('')
    setSections(initialSections)
    setTags(initialTags)
    setCategories([])
    setManufacturers(initialManufacturers)
    setOpenField(null)
    onNavigateTo(`${basePath}/1`)
  }

  const filterOptions = <T extends Option>(items: T[], inputValue: string) =>
    items.filter((item) =>
      item.title.toLowerCase().includes(inputValue.toLowerCase()),
    )

  const filteredTagItems = filterOptions(tags, tagInput)
  const filteredSectionItems = filterOptions(sections, sectionInput)
  const filteredCategoryItems = filterOptions(categories, categoryInput)
  const filteredManufacturerItems = filterOptions(
    manufacturers,
    manufacturerInput,
  )

  const handleInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
    field: FilterField,
    filteredItems: Option[],
    onSelectItem: (item: Option) => void,
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpenField(field)
      if (filteredItems.length === 0) {
        setActiveIndex(-1)
        return
      }
      setActiveIndex((index) => Math.min(index + 1, filteredItems.length - 1))
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setOpenField(field)
      if (filteredItems.length === 0) {
        setActiveIndex(-1)
        return
      }
      setActiveIndex((index) => Math.max(index - 1, 0))
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      if (activeIndex >= 0 && filteredItems[activeIndex]) {
        // Keyboard-navigated item selected
        onSelectItem(filteredItems[activeIndex])
        setOpenField(null)
        setActiveIndex(-1)
      } else {
        // No keyboard nav: resolve exact match from typed text, then search
        const inputText = (e.target as HTMLInputElement).value
        const exactMatch = filteredItems.find(
          (i) => i.title.toLowerCase() === inputText.toLowerCase(),
        )
        if (exactMatch) onSelectItem(exactMatch)
        setOpenField(null)
        handleSearch()
      }
      return
    }

    if (e.key === 'Escape') {
      setOpenField(null)
      setActiveIndex(-1)
    }
  }

  // Resolve typed text to a selection on blur (exact match → apply; no match → keep state as-is)
  const handleInputBlur = (
    inputValue: string,
    allItems: Option[],
    onSelectItem: (item: Option) => void,
    onClear: () => void,
  ) => {
    if (!inputValue) return
    const exact = allItems.find(
      (i) => i.title.toLowerCase() === inputValue.toLowerCase(),
    )
    if (exact) {
      onSelectItem(exact)
    } else {
      // Partial text that doesn't match any option — clear so the UI stays consistent
      onClear()
    }
  }

  const activeFiltersCount = hideTags
    ? [
        query.trim(),
        selectedSection,
        selectedCategory,
        selectedManufacturer,
      ].filter(Boolean).length
    : [
        query.trim(),
        selectedSection,
        selectedCategory,
        selectedTag,
        selectedManufacturer,
      ].filter(Boolean).length

  // ─── Dropdown helper ─────────────────────────────────────────────────────

  function Dropdown<T extends Option>({
    field,
    items,
    selectedSlug,
    onSelect,
    activeIndex,
  }: {
    field: FilterField
    items: T[]
    selectedSlug: string
    onSelect: (item: T) => void
    activeIndex: number
  }) {
    if (openField !== field || items.length === 0) return null
    return (
      <ul
        id={`pf-${field}-listbox`}
        className="products-filter__suggestions"
        role="listbox"
      >
        {items.map((item, index) => {
          const isActive =
            index === activeIndex ||
            (activeIndex < 0 && item.slug === selectedSlug)

          return (
            <li
              key={item.slug}
              id={`pf-${field}-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`products-filter__suggestion-item${isActive ? ' --active' : ''}`}
              onMouseDown={() => {
                onSelect(item)
                setOpenField(null)
                setActiveIndex(-1)
              }}
            >
              {item.title}
            </li>
          )
        })}
      </ul>
    )
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="products-filter" ref={filterRef}>
      <div className="products-filter-fields">
        {/* Name */}
        <div className="products-filter__field">
          <label className="products-filter__label" htmlFor="pf-query">
            Buscar por nome ou referência
          </label>
          <div className="products-filter__input-wrapper">
            <input
              id="pf-query"
              type="text"
              className="products-filter__input"
              placeholder="Ex: Aerador, 229..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            {query && (
              <button
                type="button"
                className="products-filter__input-clear"
                onClick={() => setQuery('')}
                aria-label="Limpar busca"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Tag */}
        {!hideTags && (
          <div className="products-filter__field">
            <label className="products-filter__label" htmlFor="pf-tag">
              {loadingTags ? 'Carregando setores...' : 'Setor da Indústria'}
            </label>
            <div className="products-filter__input-wrapper">
              <input
                id="pf-tag"
                type="text"
                autoComplete="off"
                role="combobox"
                aria-expanded={openField === 'tag'}
                aria-controls="pf-tag-listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  openField === 'tag' &&
                  activeIndex >= 0 &&
                  activeIndex < filteredTagItems.length
                    ? `pf-tag-option-${activeIndex}`
                    : undefined
                }
                className={`products-filter__input${selectedTag ? ' --selected' : ''}`}
                placeholder="Selecione ou digite..."
                value={tagInput}
                onFocus={() => setOpenField('tag')}
                onChange={(e) => {
                  setTagInput(e.target.value)
                  setOpenField('tag')
                }}
                onBlur={() =>
                  handleInputBlur(
                    tagInput,
                    tags,
                    (item) => handleTagChange(item.title),
                    () => {
                      setTagInput('')
                      handleTagChange('')
                    },
                  )
                }
                onKeyDown={(e) =>
                  handleInputKeyDown(e, 'tag', filteredTagItems, (item) =>
                    handleTagChange(item.title),
                  )
                }
                disabled={loadingTags}
              />
              {tagInput && !loadingTags && (
                <button
                  type="button"
                  className="products-filter__input-clear"
                  onClick={() => {
                    setTagInput('')
                    handleTagChange('')
                    setOpenField(null)
                  }}
                  aria-label="Limpar setor"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <Dropdown
              field="tag"
              items={filteredTagItems}
              selectedSlug={selectedTag}
              activeIndex={activeIndex}
              onSelect={(item) => handleTagChange(item.title)}
            />
          </div>
        )}

        {/* Section */}
        <div className="products-filter__field">
          <label className="products-filter__label" htmlFor="pf-section">
            {loadingSections ? 'Carregando tipos...' : 'Tipo de Maquinário'}
          </label>
          <div className="products-filter__input-wrapper">
            <input
              id="pf-section"
              type="text"
              autoComplete="off"
              role="combobox"
              aria-expanded={openField === 'section'}
              aria-controls="pf-section-listbox"
              aria-autocomplete="list"
              aria-activedescendant={
                openField === 'section' &&
                activeIndex >= 0 &&
                activeIndex < filteredSectionItems.length
                  ? `pf-section-option-${activeIndex}`
                  : undefined
              }
              className={`products-filter__input${selectedSection ? ' --selected' : ''}`}
              placeholder={
                sections.length === 0 ? 'Sem opções' : 'Selecione ou digite...'
              }
              value={sectionInput}
              onFocus={() => setOpenField('section')}
              onChange={(e) => {
                setSectionInput(e.target.value)
                setOpenField('section')
              }}
              onBlur={() =>
                handleInputBlur(
                  sectionInput,
                  sections,
                  (item) => handleSectionChange(item.title),
                  () => {
                    setSectionInput('')
                    handleSectionChange('')
                  },
                )
              }
              onKeyDown={(e) =>
                handleInputKeyDown(e, 'section', filteredSectionItems, (item) =>
                  handleSectionChange(item.title),
                )
              }
              disabled={loadingSections || sections.length === 0}
            />
            {sectionInput && !loadingSections && (
              <button
                type="button"
                className="products-filter__input-clear"
                onClick={() => {
                  setSectionInput('')
                  handleSectionChange('')
                  setOpenField(null)
                }}
                aria-label="Limpar seção"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <Dropdown
            field="section"
            items={filteredSectionItems}
            selectedSlug={selectedSection}
            activeIndex={activeIndex}
            onSelect={(item) => handleSectionChange(item.title)}
          />
        </div>

        {/* Category — only visible after a section is selected */}
        {selectedSection && (
          <div className="products-filter__field">
            <label className="products-filter__label" htmlFor="pf-category">
              {loadingCategories ? 'Carregando categorias...' : 'Categoria'}
            </label>
            <div className="products-filter__input-wrapper">
              <input
                id="pf-category"
                type="text"
                autoComplete="off"
                role="combobox"
                aria-expanded={openField === 'category'}
                aria-controls="pf-category-listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  openField === 'category' &&
                  activeIndex >= 0 &&
                  activeIndex < filteredCategoryItems.length
                    ? `pf-category-option-${activeIndex}`
                    : undefined
                }
                className={`products-filter__input${selectedCategory ? ' --selected' : ''}`}
                placeholder={
                  categories.length === 0
                    ? 'Sem opções'
                    : 'Selecione ou digite...'
                }
                value={categoryInput}
                onFocus={() => setOpenField('category')}
                onChange={(e) => {
                  setCategoryInput(e.target.value)
                  setOpenField('category')
                }}
                onBlur={() =>
                  handleInputBlur(
                    categoryInput,
                    categories,
                    (item) => handleCategoryChange(item.title),
                    () => handleCategoryChange(''),
                  )
                }
                onKeyDown={(e) =>
                  handleInputKeyDown(
                    e,
                    'category',
                    filteredCategoryItems,
                    (item) => handleCategoryChange(item.title),
                  )
                }
                disabled={loadingCategories}
              />
              {categoryInput && !loadingCategories && (
                <button
                  type="button"
                  className="products-filter__input-clear"
                  onClick={() => {
                    handleCategoryChange('')
                    setOpenField(null)
                  }}
                  aria-label="Limpar categoria"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <Dropdown
              field="category"
              items={filteredCategoryItems}
              selectedSlug={selectedCategory}
              activeIndex={activeIndex}
              onSelect={(item) => handleCategoryChange(item.title)}
            />
          </div>
        )}

        {/* Manufacturer */}
        <div className="products-filter__field">
          <label className="products-filter__label" htmlFor="pf-manufacturer">
            {loadingManufacturers ? 'Carregando fabricantes...' : 'Fabricante'}
          </label>
          <div className="products-filter__input-wrapper">
            <input
              id="pf-manufacturer"
              type="text"
              autoComplete="off"
              role="combobox"
              aria-expanded={openField === 'manufacturer'}
              aria-controls="pf-manufacturer-listbox"
              aria-autocomplete="list"
              aria-activedescendant={
                openField === 'manufacturer' &&
                activeIndex >= 0 &&
                activeIndex < filteredManufacturerItems.length
                  ? `pf-manufacturer-option-${activeIndex}`
                  : undefined
              }
              className={`products-filter__input${selectedManufacturer ? ' --selected' : ''}`}
              placeholder={
                manufacturers.length === 0
                  ? 'Sem opções'
                  : 'Selecione ou digite...'
              }
              value={manufacturerInput}
              onFocus={() => setOpenField('manufacturer')}
              onChange={(e) => {
                setManufacturerInput(e.target.value)
                setOpenField('manufacturer')
              }}
              onBlur={() =>
                handleInputBlur(
                  manufacturerInput,
                  manufacturers,
                  (item) => handleManufacturerChange(item.title),
                  () => {
                    setManufacturerInput('')
                    handleManufacturerChange('')
                  },
                )
              }
              onKeyDown={(e) =>
                handleInputKeyDown(
                  e,
                  'manufacturer',
                  filteredManufacturerItems,
                  (item) => handleManufacturerChange(item.title),
                )
              }
              disabled={loadingManufacturers || manufacturers.length === 0}
            />
            {manufacturerInput && !loadingManufacturers && (
              <button
                type="button"
                className="products-filter__input-clear"
                onClick={() => {
                  setManufacturerInput('')
                  handleManufacturerChange('')
                  setOpenField(null)
                }}
                aria-label="Limpar fabricante"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <Dropdown
            field="manufacturer"
            items={filteredManufacturerItems}
            selectedSlug={selectedManufacturer}
            activeIndex={activeIndex}
            onSelect={(item) => handleManufacturerChange(item.title)}
          />
        </div>
      </div>

      <div className="products-filter-action">
        {activeFiltersCount > 0 && (
          <button
            type="button"
            className="products-filter__clear"
            onClick={handleClear}
            title="Limpar todos os filtros"
          >
            <X size={14} />
            Limpar Filtros
          </button>
        )}
        <button
          type="button"
          className={`products-filter__submit${isPending ? ' --loading' : ''}`}
          onClick={handleSearch}
          disabled={isPending}
        >
          <Search size={16} />
          Buscar
        </button>
      </div>
    </div>
  )
}
